/**
 * Books — Approved books with word analytics
 *
 * Shows scanned_books with superadmin_approved=true.
 * Displays unique word count, module breakdowns (placeholder 0s for now),
 * and a "View Lexicon" button that opens the book's unique word list.
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Search, BookOpen, Loader2, ExternalLink } from 'lucide-react';
import { supabase } from '@/lib/supabase/client';
import { glassStyles } from '@/lib/design';

interface ApprovedBook {
  id: string;
  title: string;
  author: string | null;
  page_count: number;
  unique_words: string[];
  word_count: number;
}

export function BooksPage() {
  const [books, setBooks] = useState<ApprovedBook[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [lexiconBookId, setLexiconBookId] = useState<string | null>(null);

  const loadBooks = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch approved books
      const { data: booksData, error: booksErr } = await supabase
        .from('scanned_books')
        .select('id, title, author')
        .eq('superadmin_approved', true)
        .is('deleted_at', null)
        .order('title');

      if (booksErr) throw new Error(booksErr.message);
      if (!booksData?.length) { setBooks([]); setLoading(false); return; }

      // Fetch pre-computed lexicon from book_lexicon table
      const bookIds = booksData.map(b => b.id);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data: lexiconData } = await (supabase as any)
        .from('book_lexicon')
        .select('book_id, word, frequency, in_turn_lexicon, turn_code')
        .in('book_id', bookIds)
        .order('word');

      // Group words by book
      const wordsByBook = new Map<string, { word: string; frequency: number; inLexicon: boolean }[]>();
      for (const entry of lexiconData || []) {
        const list = wordsByBook.get(entry.book_id) || [];
        list.push({ word: entry.word, frequency: entry.frequency, inLexicon: entry.in_turn_lexicon });
        wordsByBook.set(entry.book_id, list);
      }

      const result: ApprovedBook[] = booksData.map(b => {
        const words = wordsByBook.get(b.id) || [];
        return {
          id: b.id,
          title: b.title,
          author: b.author,
          page_count: 0,
          unique_words: words.map(w => w.word),
          word_count: words.length,
        };
      });

      setBooks(result);
    } catch (err) {
      console.error('Failed to load books:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadBooks(); }, [loadBooks]);

  const filtered = useMemo(() => {
    if (!searchTerm) return books;
    const q = searchTerm.toLowerCase();
    return books.filter(b =>
      b.title.toLowerCase().includes(q) ||
      (b.author && b.author.toLowerCase().includes(q))
    );
  }, [books, searchTerm]);

  // Module placeholders (0 for now — modules not yet defined)
  const modules = [1, 2, 3, 4, 5];

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="w-8 h-8 text-[#1A1A1A] animate-spin" />
      </div>
    );
  }

  // Lexicon modal
  const lexiconBook = books.find(b => b.id === lexiconBookId);

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-[#1C1C1E]">Books</h2>
        <p className="text-sm text-[#8E8E93] mt-0.5">
          {books.length} approved book{books.length !== 1 ? 's' : ''}
        </p>
      </div>

      {/* Search */}
      <div className="relative max-w-sm mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8E8E93]" />
        <input
          type="text"
          placeholder="Search title or author..."
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          className="w-full pl-10 pr-4 py-2.5 bg-white border border-black/[0.08] rounded-xl text-[#1C1C1E] text-sm placeholder-[#8E8E93] focus:outline-none focus:border-[#1A1A1A]"
        />
      </div>

      {/* Empty state */}
      {filtered.length === 0 && (
        <div className="rounded-2xl p-8 text-center" style={glassStyles.card}>
          <p className="text-[#8E8E93] text-sm">
            {books.length === 0
              ? 'No books have been SuperAdmin Approved yet.'
              : 'No books match your search.'}
          </p>
        </div>
      )}

      {/* Table */}
      {filtered.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-black/[0.06]">
                <th className="text-left py-3 px-3 font-medium text-[#636366]">Book</th>
                <th className="text-center py-3 px-2 font-medium text-[#636366] w-20">Words</th>
                {modules.map(m => (
                  <th key={m} className="text-center py-3 px-2 font-medium text-[#636366] w-16">M{m}</th>
                ))}
                <th className="text-center py-3 px-2 font-medium text-[#636366] w-24">Lexicon</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(book => (
                <tr key={book.id} className="border-b border-black/[0.03] hover:bg-black/[0.01]">
                  <td className="py-3 px-3">
                    <p className="font-medium text-[#1C1C1E] truncate max-w-xs">{book.title}</p>
                    {book.author && <p className="text-xs text-[#8E8E93]">{book.author}</p>}
                  </td>
                  <td className="text-center py-3 px-2">
                    <span className="font-mono text-[#1C1C1E]">{book.word_count}</span>
                  </td>
                  {modules.map(m => (
                    <td key={m} className="text-center py-3 px-2">
                      <span className="font-mono text-[#8E8E93]">0</span>
                    </td>
                  ))}
                  <td className="text-center py-3 px-2">
                    <button
                      onClick={() => setLexiconBookId(book.id)}
                      className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-[#111111]/10 text-[#1A1A1A] text-xs font-medium hover:bg-[#111111]/20 mx-auto"
                    >
                      <BookOpen className="w-3 h-3" />
                      View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Lexicon modal */}
      {lexiconBook && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setLexiconBookId(null)}>
          <div
            className="bg-white rounded-2xl max-w-lg w-full mx-4 max-h-[80vh] flex flex-col shadow-xl"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-4 border-b border-black/[0.06]">
              <div>
                <h3 className="font-semibold text-[#1C1C1E]">{lexiconBook.title}</h3>
                <p className="text-xs text-[#8E8E93]">{lexiconBook.word_count} unique words</p>
              </div>
              <button
                onClick={() => setLexiconBookId(null)}
                className="px-3 py-1.5 rounded-lg bg-black/[0.03] text-[#636366] text-xs hover:bg-black/[0.06]"
              >
                Close
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              <div className="flex flex-wrap gap-2">
                {lexiconBook.unique_words.map(word => (
                  <span
                    key={word}
                    className="px-2.5 py-1 text-sm rounded-lg bg-[#F2F2F7] text-[#1C1C1E] font-mono"
                  >
                    {word}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default BooksPage;
